import {
  SyntheticEvent,
  FocusEvent,
  forwardRef,
  ForwardRefExoticComponent,
  ChangeEvent,
  RefAttributes,
  MutableRefObject,
  MouseEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import cns from 'classnames';

import { ReactComponent as Eye } from 'icons/eye.svg';
import { ReactComponent as EyeOff } from 'icons/eye-off.svg';
import styles from './Input.module.scss';

export interface IInputProps extends RefAttributes<HTMLInputElement> {
  htmlType?: 'text' | 'password';
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event?: FocusEvent<HTMLElement>) => void;
  onFocus?: (event?: FocusEvent<HTMLElement>) => void;
  onClick?: (event?: SyntheticEvent) => void;
  name: string;
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
}

const Input: ForwardRefExoticComponent<IInputProps> = forwardRef(
  (
    {
      placeholder = '',
      value = '',
      htmlType = 'text',
      onChange,
      name,
      hasError,
      disabled,
      onClick,
      onFocus,
      onBlur,
      autoFocus = false,
      ...rest
    },
    ref
  ) => {
    const _inputRef: MutableRefObject<null | HTMLInputElement> = useRef(null);
    useImperativeHandle(ref, () => _inputRef.current as HTMLInputElement);

    const [isPassword, setIsPassword] = useState(htmlType === 'password');
    const [isActive, setIsActive] = useState(false);

    const EyeIcon = useMemo(() => (isPassword ? EyeOff : Eye), [isPassword]);

    const handleDownIconInner = (event: MouseEvent) => {
      event.preventDefault();
    };
    const handleIsPasswordToggle = () => setIsPassword((value) => !value);
    const handleClick = (e: SyntheticEvent) => {
      _inputRef.current?.focus();
      onClick && onClick(e);
    };
    const handleFocus = (e: FocusEvent<HTMLElement>) => {
      setIsActive(true);
      onFocus && onFocus(e);
    };
    const handleBlur = (e: FocusEvent<HTMLElement>) => {
      setIsActive(false);
      onBlur && onBlur(e);
    };
    return (
      <div
        className={cns(styles.wrapper, {
          [styles.disabled]: disabled,
          [styles.active]: isActive,
          [styles.error]: hasError,
        })}
        onClick={handleClick}
        tabIndex={-1}
      >
        <div className={styles.input_inner}>
          <input
            {...rest}
            onFocus={handleFocus}
            onBlur={handleBlur}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            ref={_inputRef}
            type={isPassword ? 'password' : 'text'}
            className={styles.input}
            autoComplete="off"
            autoFocus={autoFocus}
          />
        </div>
        {htmlType === 'password' && (
          <div className={styles.icon_inner} onMouseDown={handleDownIconInner}>
            <EyeIcon className={styles.password_icon} onClick={handleIsPasswordToggle} />
          </div>
        )}
      </div>
    );
  }
);

export default Input;
